import { Fragment } from 'react';
import { NavLink } from 'react-router';
import { LayoutDashboard, Package, Boxes, Images, BarChart3, Palette, Sparkles, Settings2, Tags, Link2, ListOrdered, ScrollText, FlaskConical, Plug, Code, Shield, Users, Truck, BadgePercent, Webhook, Target, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useMe } from '../auth/MeContext';
import type { ConsoleSection, OperatorFlag } from '../lib/api/operatorConsole';

/** Purely a display cluster for the menu — independent of the `section`
 *  permission gate below. Absent = pinned above every group (Dashboard, Metrics). */
type NavGroup = 'Catalog' | 'Presentation' | 'Players' | 'Developer' | 'Account';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
  end?: boolean;
  /** Rendered only when the operator has this flag turned on. */
  flag?: OperatorFlag;
  /** Console section this page belongs to. Absent = open to every console
   *  user (Dashboard, API docs). */
  section?: ConsoleSection;
  navGroup?: NavGroup;
}

const ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  { to: '/metrics', label: 'Metrics', Icon: BarChart3, flag: 'allow_metrics', section: 'metrics' },
  { to: '/packs', label: 'Packs', Icon: Package, flag: 'allow_packs_view', section: 'packs', navGroup: 'Catalog' },
  // Self-service pack builder: the operator flag says the company may build
  // packs, the section says this person may open the screen.
  { to: '/my-packs', label: 'My Packs', Icon: Boxes, flag: 'allow_pack_builder', section: 'pack_builder', navGroup: 'Catalog' },
  { to: '/configs', label: 'Configs', Icon: Settings2, section: 'offers', navGroup: 'Catalog' },
  { to: '/category-configs', label: 'Categories', Icon: Tags, section: 'categories', navGroup: 'Catalog' },
  // Original artwork of the operator's packs for their own marketing.
  { to: '/media-kit', label: 'Media Kit', Icon: Images, flag: 'allow_media_kit', section: 'media_kit', navGroup: 'Catalog' },
  { to: '/themes', label: 'Themes', Icon: Palette, section: 'themes', navGroup: 'Presentation' },
  // The opening mechanic is a feature the operator is granted; the screen
  // itself belongs to the same section as Themes.
  { to: '/animation', label: 'Pack Opening', Icon: Sparkles, flag: 'allow_animation_settings', section: 'themes', navGroup: 'Presentation' },
  { to: '/footer-links', label: 'Links', Icon: Link2, section: 'links', navGroup: 'Presentation' },
  { to: '/sessions', label: 'Sessions', Icon: ListOrdered, section: 'players', navGroup: 'Players' },
  { to: '/shippings', label: 'Deliveries', Icon: Truck, section: 'shipping', navGroup: 'Players' },
  // Delivery prices the operator funds: granted per operator by the flag, and
  // to a person with the same section as Deliveries.
  { to: '/shipping-promotions', label: 'Shipping Promotions', Icon: BadgePercent, flag: 'allow_shipping_promotions', section: 'shipping', navGroup: 'Players' },
  // The registry drives settlement figures, so an operator who may not edit
  // it still gets to see what we exclude (read-only page).
  { to: '/test-players', label: 'Test Players', Icon: FlaskConical, section: 'test_players', navGroup: 'Players' },
  { to: '/integration', label: 'Integration', Icon: Plug, section: 'integration', navGroup: 'Developer' },
  // Grantable, unlike Integration next to it: notifications move no money, so
  // an owner can hand this one to whoever does the integrating.
  { to: '/webhooks', label: 'Webhooks', Icon: Webhook, section: 'webhooks', navGroup: 'Developer' },
  // The ids go to the operator's own ad accounts — a marketing job, granted on its own.
  { to: '/tracking-pixels', label: 'Tracking Pixels', Icon: Target, flag: 'allow_tracking_pixels', section: 'tracking', navGroup: 'Developer' },
  { to: '/api', label: 'API', Icon: Code, navGroup: 'Developer' },
  { to: '/callback-logs', label: 'Callback Logs', Icon: ScrollText, section: 'logs', navGroup: 'Developer' },
  // Open to everyone: the page leads with your own sign-in IPs and your own
  // authenticator. The operator-wide perimeter inside it is served to the
  // owner alone.
  { to: '/security', label: 'Security', Icon: Shield, navGroup: 'Account' },
  { to: '/team', label: 'Team', Icon: Users, section: 'team', navGroup: 'Account' },
];

/**
 * Vertical primary navigation for the operator console. Lives inside AppShell.
 * Active state — driven by react-router NavLink. Bottom area shows the
 * current user email + Logout button.
 */
export function Sidebar() {
  const auth = useAuth();
  const { can, has, failed } = useMe();

  // Two independent gates: the operator-level feature flag decides whether
  // the page exists at all, the section permission decides whether this
  // person may open it.
  const items = ITEMS.filter(
    (item) => (!item.flag || has(item.flag)) && (!item.section || can(item.section)),
  );

  return (
    <nav
      aria-label="Primary"
      className="flex h-full w-56 shrink-0 flex-col border-r border-[var(--ui-stroke-card,#EAEAEB)] bg-[var(--ui-bg-paper,#FFFFFF)] p-3"
    >
      <div className="px-3 pb-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--ui-text-subtext,#878B95)]">
        Operator Console
      </div>

      <div className="flex flex-1 flex-col gap-1">
        {items.map(({ to, label, Icon, end, navGroup }, index) => {
          const showHeader = navGroup && navGroup !== items[index - 1]?.navGroup;
          return (
            <Fragment key={to}>
              {showHeader && (
                <div className="mt-3 px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--ui-text-subtext,#878B95)]">
                  {navGroup}
                </div>
              )}
              <NavLink
                to={to}
                end={end ?? false}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-2 rounded-md px-3 py-2 text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-[var(--ui-bg-card,#F2F2F2)] text-[var(--ui-text-primary,#15181E)]'
                      : 'text-[var(--ui-text-subtext,#878B95)] hover:bg-[var(--ui-bg-card,#F2F2F2)] hover:text-[var(--ui-text-primary,#15181E)]',
                  ].join(' ')
                }
              >
                <Icon size={16} aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            </Fragment>
          );
        })}
      </div>

      {/* Without this the short menu reads as "my sections vanished" rather
       *  than "we could not load them". */}
      {failed && (
        <div className="mx-1 mb-2 rounded-md bg-[var(--ui-bg-card,#F2F2F2)] px-2 py-2 text-[11px] text-[var(--ui-text-subtext,#878B95)]">
          Couldn’t load your access — reload the page to try again.
        </div>
      )}

      <div className="mt-2 border-t border-[var(--ui-stroke-card,#EAEAEB)] pt-3">
        {auth.currentUser?.email && (
          <div
            className="truncate px-3 pb-2 text-[11px] text-[var(--ui-text-subtext,#878B95)]"
            title={auth.currentUser.email}
          >
            {auth.currentUser.email}
          </div>
        )}
        <button
          type="button"
          onClick={() => void auth.logout()}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-[13px] font-medium text-[var(--ui-text-subtext,#878B95)] transition-colors hover:bg-[var(--ui-bg-card,#F2F2F2)] hover:text-[var(--ui-text-primary,#15181E)]"
        >
          <LogOut size={16} aria-hidden="true" />
          <span>Logout</span>
        </button>
      </div>
    </nav>
  );
}
